import { FC, useEffect, useMemo, useState } from "react";
import { Navigate, useParams, useSearchParams } from "react-router-dom";
import { Container } from "@mui/material";
import { useUserStore } from "../../../stores/user.store";
import { useGetProgramsByIds } from "../../../hooks/useBeneficiaries";
import { useCurrentUser } from "../../../hooks/useCurrentUser";
import { Program } from "../../../domain/program.interface";
import EnrollmentSummaryTab from "./tabs/EnrollmentSummaryTab";
import ProgramActivitySummaryTab from "./tabs/ProgramActivitySummaryTab";
import VisitDetailTab from "./tabs/VisitDetailTab";
import AnalyticsAssistantTab from "./tabs/AnalyticsAssistantTab";

interface TabDef {
	id: string;
	label: string;
	/** URL slug — each report is deep-linked at /reports/center/<slug>. */
	slug: string;
	/** Per-tab menu-action code (each tab is a separate, individually granted option). */
	code: string;
}

const TAB_DEFS: TabDef[] = [
	{
		id: "assistant",
		label: "Assistant",
		slug: "assistant",
		code: "REP_DASH_ASSISTANT_VIEW",
	},
	{
		id: "enrollment",
		label: "Enrollment summary",
		slug: "enrollment",
		code: "REP_DASH_ENROLLMENT_VIEW",
	},
	{
		id: "activity",
		label: "Program Summary",
		slug: "program-summary",
		code: "REP_DASH_ACTIVITY_VIEW",
	},
	{
		id: "visit",
		label: "Visit detail",
		slug: "visit-detail",
		code: "REP_DASH_VISIT_VIEW",
	},
];

// Legacy umbrella code: a role that still holds it sees every tab (back-compat
// with the original single Reports Center grant, before per-tab codes existed).
const UMBRELLA_CODE = "REP_DASH_VIEW";

const ReportsDashboard: FC = () => {
	const { userInfo, userHasAccessToCode } = useUserStore();
	// SIMS-7884: revalidate the user's assignments on every entry to this screen,
	// not just when the session-wide staleTime has lapsed. Every report here is
	// filtered by the program list below, and in the Assistant tab a missing
	// program does not read as a missing filter option — it reads as the
	// assistant refusing a valid instruction. One cheap GET per navigation to
	// this screen (a deliberate user action, not a render) buys an exact list.
	useCurrentUser({ refetchOnMount: "always" });
	// SIMS-7390: the program filter must list only the programs assigned to the
	// logged-in user, not every program in the account. SIMS-7884: `userInfo` is
	// now kept fresh by useCurrentUser above, so this stays correctly scoped
	// without being frozen at login.
	const programIds = useMemo(
		() => userInfo?.programs?.map((p) => p.programId) ?? [],
		[userInfo],
	);
	const { data: programs, isLoading: isProgramsLoading } =
		useGetProgramsByIds(programIds);
	const [searchParams] = useSearchParams();
	const { report: reportSlug } = useParams<{ report?: string }>();

	// Filters (incl. program) start EMPTY on every entry to a report link — no
	// selection is carried over between visits. Kept independent per report tab.
	// SIMS-7990: the selection is now a LIST per tab — the reports this Epic feeds
	// (Program Activity Summary, Visit Detail) accept more than one program. Held as
	// string[] because a single string cannot represent a multi-program selection no
	// matter what the Select renders: that was the real reason only one program could
	// be picked, not the `multiple` prop.
	const [programByTab, setProgramByTab] = useState<Record<string, string[]>>(
		{},
	);

	// Clear, per tab, any program that no longer exists once programs load.
	useEffect(() => {
		if (isProgramsLoading || !programs) return;
		const validIds = new Set(programs.map((p) => p._id));
		setProgramByTab((prev) => {
			let changed = false;
			const next = { ...prev };
			for (const [tab, pids] of Object.entries(prev)) {
				// Drop only the stale ids, keeping the rest of the selection — a
				// program disappearing must not clear the other choices.
				const kept = (pids ?? []).filter((pid) => validIds.has(pid));
				if (kept.length !== (pids ?? []).length) {
					next[tab] = kept;
					changed = true;
				}
			}
			return changed ? next : prev;
		});
	}, [programs, isProgramsLoading]);

	// useGetProgramsByIds returns the beneficiary-domain Program shape; the
	// Reports tabs only read _id/name, so treat it as the program-domain Program
	// (same cast as Beneficiary/ListFilters).
	//
	// Sorted by name (SIMS-7990, Epic Hi-Fi): the option list is specified as
	// "Assigned programs only, alphabetical", and the results header reads
	// "N programs · alphabetical". The API returns them in its own order, so
	// without this the chips render in arrival order — which looked correct only
	// because no test asserted the ordering.
	//
	// Sorted here rather than in each tab because this is the single source all
	// three tabs receive as `programs`. `localeCompare` (not `<`) so accented and
	// non-ASCII program names collate correctly rather than being pushed to the end.
	const programList = useMemo<Program[]>(
		() =>
			[...((programs ?? []) as unknown as Program[])].sort((a, b) =>
				(a.name ?? "").localeCompare(b.name ?? ""),
			),
		[programs],
	);

	// Each tab is a separately-granted menu option. Show only the tabs the user
	// has a code for; the legacy umbrella code grants all of them.
	const hasUmbrella = userHasAccessToCode(UMBRELLA_CODE);
	const visibleTabs = TAB_DEFS.filter(
		(t) => hasUmbrella || userHasAccessToCode(t.code),
	);

	// Each report has its OWN URL: /reports/center/<slug>. Resolve the active
	// report from the path slug first, then fall back to the legacy ?tab=<id>
	// query param, then the first report the user can see.
	const requestedTab = searchParams.get("tab");
	const activeTabId =
		visibleTabs.find((t) => t.slug === reportSlug)?.id ??
		visibleTabs.find((t) => t.id === requestedTab)?.id ??
		visibleTabs[0]?.id ??
		"";

	// SIMS-7390: a program is always selected. SIMS-7990 AC2 widens that default to
	// EVERY program the user is assigned, not just the first — `programList` is
	// already the caller's assigned set (resolved from userInfo.programs above), so
	// this cannot pre-select something they are not entitled to.
	useEffect(() => {
		if (isProgramsLoading || !programList.length || !activeTabId) return;
		setProgramByTab((prev) =>
			prev[activeTabId]?.length
				? prev
				: { ...prev, [activeTabId]: programList.map((p) => p._id) },
		);
	}, [programList, isProgramsLoading, activeTabId]);

	if (visibleTabs.length === 0) {
		return <Navigate to="/unauthorized" replace />;
	}

	/**
	 * Each report tab gets its OWN program state + change handlers.
	 *
	 * `programIds` / `onProgramsChange` are the multi-program pair (SIMS-7990).
	 * `programId` / `onProgramChange` are kept as the first-selected view of the same
	 * state so the reports that are still single-program (Enrollment Summary, Event
	 * activity, …) keep working untouched — this Epic only covers Program Activity
	 * Summary and Visit Detail, and widening every tab at once is not its scope.
	 */
	const programPropsFor = (
		tabId: string,
	): {
		programId: string;
		programIds: string[];
		programs: Program[];
		onProgramChange: (id: string) => void;
		onProgramsChange: (ids: string[]) => void;
	} => ({
		programId: programByTab[tabId]?.[0] ?? "",
		programIds: programByTab[tabId] ?? [],
		programs: programList,
		onProgramChange: (id: string) =>
			setProgramByTab((prev) => ({ ...prev, [tabId]: id ? [id] : [] })),
		onProgramsChange: (ids: string[]) =>
			setProgramByTab((prev) => ({ ...prev, [tabId]: ids })),
	});

	return (
		<Container maxWidth="xl" sx={{ py: 4 }}>
			{/* Each menu option is its own report and deep-links here with
			    ?tab=<id>; render ONLY that report — no tab strip. */}
			{activeTabId === "assistant" && (
				<AnalyticsAssistantTab programs={programList} />
			)}
			{activeTabId === "enrollment" && (
				<EnrollmentSummaryTab {...programPropsFor("enrollment")} />
			)}
			{activeTabId === "activity" && (
				<ProgramActivitySummaryTab {...programPropsFor("activity")} />
			)}
			{activeTabId === "visit" && (
				<VisitDetailTab {...programPropsFor("visit")} />
			)}
		</Container>
	);
};

export default ReportsDashboard;
